import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "container-fluid admin-page" }

import deviceApi from 'src/apis/device';
import group2Api from 'src/apis/group2';
import group3Api from 'src/apis/group3';
import {
  computed,
  onMounted,
  reactive,
} from 'vue';
import type { Device } from 'src/models/device';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';
import type { Lov } from 'src/models/master';
import type { DeviceInputModel } from 'src/models/device';
import { createDeviceInitInputModel } from 'src/models/device';
import type { AdminDeviceParams } from 'src/models/apis/device/adminDeviceRequest';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import { splitByLineBreak } from 'src/lib/utils';
import type { AxiosError } from 'axios';
import { useReactiveMasters } from 'src/composables/useMasters';
import { useAuth } from 'src/composables/useAuth';
import type { User } from 'src/models/user';
import DeviceTable from 'src/components/Admin/Device/DeviceTable.vue';
import DeviceEditModal from 'src/components/Admin/Device/DeviceEditModal.vue';

interface DevicesSearchState {
  devices: Device[];
  groups2: AdminGroup2[];
  groups3: AdminGroup3[];
  carKinds: Lov[];
}

interface DevicesEditState {
  editingDevice: DeviceInputModel;
  showEditModal: boolean;
  isEditMode: boolean;
  isRequesting: boolean;
  showErrorModal: boolean;
  httpErrorMsgs: string[];
}

const errorModalTitle = 'エラー';
const errorModalMsg = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';

export default /*@__PURE__*/_defineComponent({
  __name: 'Devices',
  setup(__props) {

const searchState = reactive<DevicesSearchState>({
  devices: [],
  groups2: [],
  groups3: [],
  carKinds: [],
});
const editState = reactive<DevicesEditState>({
  editingDevice: createDeviceInitInputModel(),
  showEditModal: false,
  isEditMode: false,
  isRequesting: false,
  showErrorModal: false,
  httpErrorMsgs: [],
});

// inject() は setup コンテキストでのみ有効なため、ここでキャッシュする
const { masters, resolveMastersPromise } = useReactiveMasters();

// 認証済み（管理画面）でのみ描画されるため user は非 null
const userState = useAuth().user as User;

const availableGroups2 = computed<AdminGroup2[]>(() => {
  if (userState.hasRoleOperationsAdmin) {
    return searchState.groups2;
  } else if (userState.hasRoleDepartmentAdmin) {
    return searchState.groups2.filter(g2 => g2.id === userState.groupInfo?.g2id);
  }
  return [];
});
const availableGroups3 = computed<AdminGroup3[]>(() => {
  if (userState.hasRoleOperationsAdmin) {
    return searchState.groups3;
  } else if (userState.hasRoleBranchAdmin) {
    return searchState.groups3.filter(g3 => g3.parentId === userState.groupInfo?.g2id);
  } else if (userState.hasRoleDepartmentAdmin) {
    return searchState.groups3.filter(g3 => g3.id === userState.groupInfo?.g3id);
  }
  return [];
});

const loadDevices = async(): Promise<void> => {
  try {
    const devices = await deviceApi.adminIndex();
    if (!devices) return;

    searchState.devices = devices;
  } catch {
    editState.showErrorModal = true;
  }
};

const openCreateModal = (): void => {
  editState.isEditMode = false;
  editState.editingDevice = createDeviceInitInputModel();
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};

const openUpdateModal = (device: Device): void => {
  editState.isEditMode = true;
  editState.editingDevice = {
    id: device.id,
    deviceId: device.deviceId,
    carName: device.carName,
    carKind: device.carKind,
    group2Id: device.groupInfo.g2id,
    groupId: device.groupInfo.g3id,
    stoppedUseOn: device.stoppedUseOn ? new Date(device.stoppedUseOn) : null,
    bikou1: splitByLineBreak(device.bikou1).join('\n'),
  };
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};

const onCreateDevice = async(payload: AdminDeviceParams): Promise<void> => {
  editState.isRequesting = true;
  try {
    await deviceApi.adminCreate(payload);
    editState.httpErrorMsgs = [];
    await loadDevices();
    editState.showEditModal = false;
  } catch (err) {
    editState.httpErrorMsgs = getErrorMessages(err as AxiosError);
  } finally {
    editState.isRequesting = false;
  }
};

const onUpdateDevice = async(deviceId: string, payload: AdminDeviceParams): Promise<void> => {
  editState.isRequesting = true;
  try {
    await deviceApi.adminUpdate(deviceId, payload);
    editState.httpErrorMsgs = [];
    await loadDevices();
    editState.showEditModal = false;
  } catch (err) {
    editState.httpErrorMsgs = getErrorMessages(err as AxiosError);
  } finally {
    editState.isRequesting = false;
  }
};

const onDestroyDevice = async(device: Device): Promise<void> => {
  try {
    await deviceApi.adminDestroy(device.deviceId);
    await loadDevices();
  } catch {
    editState.showErrorModal = true;
  }
};

onMounted(async() => {
  await resolveMastersPromise();
  searchState.carKinds = masters.lov.carKinds;
  try {
    const [groups2, groups3] = await Promise.all([
      group2Api.adminIndex(),
      group3Api.adminIndex(),
    ]);
    searchState.groups2 = groups2;
    searchState.groups3 = groups3;
  } catch {
    editState.showErrorModal = true;
  }
  await loadDevices();
});


return (_ctx: any,_cache: any) => {
  const _component_Modal = _resolveComponent("Modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", { class: "title-area" }, [
      _cache[3] || (_cache[3] = _createElementVNode("h2", null, "車載器管理", -1)),
      _createElementVNode("div", { class: "create-btn" }, [
        _createElementVNode("button", {
          class: "btn btn-info",
          onClick: openCreateModal
        }, [...(_cache[2] || (_cache[2] = [
          _createElementVNode("i", { class: "fas fa-plus" }, null, -1),
          _createElementVNode("span", { class: "ml-1" }, "新規作成", -1)
        ]))])
      ])
    ]),
    _createVNode(DeviceTable, {
      devices: searchState.devices,
      "car-kinds": searchState.carKinds,
      onEdit: openUpdateModal,
      onDestroy: onDestroyDevice,
      onSearch: loadDevices
    }, null, 8, ["devices", "car-kinds"]),
    (editState.showEditModal)
      ? (_openBlock(), _createBlock(DeviceEditModal, {
          key: 0,
          "is-edit-mode": editState.isEditMode,
          device: editState.editingDevice,
          "available-groups2": availableGroups2.value,
          "available-groups3": availableGroups3.value,
          "car-kinds": searchState.carKinds,
          "is-requesting": editState.isRequesting,
          "http-error-msgs": editState.httpErrorMsgs,
          onCreate: onCreateDevice,
          onUpdate: onUpdateDevice,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (editState.showEditModal = false))
        }, null, 8, ["is-edit-mode", "device", "available-groups2", "available-groups3", "car-kinds", "is-requesting", "http-error-msgs"]))
      : _createCommentVNode("", true),
    (editState.showErrorModal)
      ? (_openBlock(), _createBlock(_component_Modal, {
          key: 1,
          title: errorModalTitle,
          "show-bottom-dismiss-button": false,
          onClose: _cache[1] || (_cache[1] = ($event: any) => (editState.showErrorModal = false))
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("p", {
                class: "p2 font-mtx-desc",
                innerHTML: errorModalMsg
              })
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})